import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { FormField } from "@/components/ui/form"
import { TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { textAreaDefaultMaxLength } from "@/models/common"
import { DeBillingSchemaType } from "@/modules/common/billing/components/billing-form/de/schema/de-billing-schema"
import { NotBillableReasonValues } from "@/types/__generated__/union-types"

const DeBillingSection = ({
  disabled,
  isAdministrativeRequest,
}: {
  disabled?: boolean
  isAdministrativeRequest?: boolean
}) => {
  const { t } = useTranslation()

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<DeBillingSchemaType>()
  const isNotBillable = useWatch({ control, name: "isNotBillable" })
  const notBillableReason = useWatch({ control, name: "notBillableReason" })

  return (
    <div className="flex flex-col gap-4">
      <div className="text-base font-medium">{t("patient.conclude.billing.billing")}</div>
      {!isAdministrativeRequest && (
        <div className="flex justify-between">
          <FormField
            control={control}
            name="isNotBillable"
            render={({ field: { value, onChange } }) => (
              <DetailCheckbox
                checked={value}
                data-testid="not-billable-checkbox"
                onCheckedChange={onChange}
                disabled={disabled}>
                {t("patient.conclude.billing.notBillable")}
              </DetailCheckbox>
            )}
          />
        </div>
      )}
      {isNotBillable ? (
        <>
          <FormField
            control={control}
            name="notBillableReason"
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                className="gap-4"
                onValueChange={onChange}
                defaultValue={value}
                value={value}
                data-testid="not-billable-reasons"
                disabled={disabled}>
                {NotBillableReasonValues.map((notBillableReason) => (
                  <RadioGroupItem
                    value={notBillableReason}
                    key={notBillableReason}
                    data-testid={notBillableReason.toLowerCase()}>
                    <RadioGroupLabel>
                      {t(`patient.conclude.billing.notBillableReasonList.${notBillableReason}`)}
                    </RadioGroupLabel>
                  </RadioGroupItem>
                ))}
              </RadioGroup>
            )}
          />
          {notBillableReason === "OTHER_REASON" && (
            <TextArea
              {...register("reason")}
              label={t("patient.conclude.billing.reason")}
              error={errors.reason?.message}
              data-testid="reason-input"
              translateError
              required
              textLimit={textAreaDefaultMaxLength}
              disabled={disabled}
            />
          )}
        </>
      ) : (
        <FormField
          control={control}
          name="isVisualSymptomExamination"
          render={({ field: { value, onChange } }) => (
            <DetailCheckbox
              data-testid="visual-symptom-based-consultation-checkbox"
              onCheckedChange={onChange}
              checked={value}
              disabled={disabled}>
              {t("patient.conclude.billing.performedVisualSymptomBasedConsultation")}
            </DetailCheckbox>
          )}
        />
      )}
    </div>
  )
}
export { DeBillingSection }
